import { Inject, Injectable, Optional } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { environment } from '../environments/environment';
import {
	AccountMode,
	DEPLOY_STEPS,
	DeploymentSnapshot,
	InterestForm,
	AppLauncherStep,
	AppLauncherTab,
	TABS,
} from './app-launcher.models';
import { BetaAuthService } from './services/beta-auth.service';
import { FirebaseAuthService } from './services/firebase-auth.service';

const SESSION_EXPIRED_NOTICE =
	'Your session ended. Sign in again to get back to your apps.';

const PASSWORD_UPDATED_NOTICE =
	'Your password was updated. Sign in with your new password.';

/**
 * Single source of truth for the standalone App Launcher shell.
 *
 * Mirrors the pilot prototype's client-side state machine: the landing,
 * account, and expanded-access flows are guided journeys, while the apps home
 * and in-app management surfaces are provided by the embedded
 * `central-web-apps-wc` web component (see AppsHomeComponent).
 */
@Injectable({ providedIn: 'root' })
export class AppLauncherStateService {
	public step: AppLauncherStep = 'login';
	public appTab: AppLauncherTab = 'source';
	public accountMode: AccountMode = 'create';

	public deployIndex = 0;
	public deployment: DeploymentSnapshot | null = null;
	public sourceConnected = false;
	public limitVisible = false;

	public feedbackOpen = false;
	public feedbackSubmitted = false;

	public interestSubmitted = false;

	/** Explains an involuntary return to the account screen (see {@link expireSession}). */
	public sessionNotice = '';

	/** Opaque token from a `?reset=` email link; empty when not confirming a reset. */
	public passwordResetToken = '';

	public appName = 'Happy Lab';
	public githubOrg = 'garrettb-imh';
	public repository = 'cloud-cost-coach';
	public branch = 'main';
	public accountEmail = '';

	public interestForm: InterestForm = {
		name: '',
		email: '',
		capacity: '3-5 projects',
		audience: 'Client projects',
		notes: 'Testing App Launcher for a few Lovable-built client applications.',
	};

	private deployTimers: ReturnType<typeof setTimeout>[] = [];

	constructor(
		private readonly betaAuth: BetaAuthService,
		private readonly firebaseAuth: FirebaseAuthService,
		@Optional() @Inject(DOCUMENT) doc: Document | null = null,
	) {
		this.passwordResetToken = this.readResetTokenFromQuery(doc);

		if (this.betaAuth.restoreSession()) {
			const user = this.betaAuth.getUser();
			if (user) {
				this.accountEmail = user.email;
				this.interestForm.email = user.email;
			}
			if (this.passwordResetToken) {
				this.betaAuth.clearSession();
				this.step = 'login';
			} else {
				this.step = 'create-app';
			}
		} else {
			const prefill = this.readEmailFromQuery(doc);
			if (prefill) {
				this.accountEmail = prefill;
				this.accountMode = 'create';
			}
			this.step = 'login';
		}
	}

	/**
	 * Prefill the account email from a `?email=` query param handed off by the
	 * labs-landing marketing form. Only a well-formed address is accepted so the
	 * value cannot be used to inject arbitrary content into the form; the param
	 * seeds the field only — it never authenticates or advances a step on its own.
	 */
	private readEmailFromQuery(doc: Document | null): string {
		const search = doc?.defaultView?.location?.search ?? '';
		if (!search) {
			return '';
		}
		const raw = new URLSearchParams(search).get('email') ?? '';
		const email = raw.trim();
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
	}

	/**
	 * Read an opaque password-reset token from `?reset=`. Empty or whitespace-
	 * bearing values are ignored so the param cannot be used as an open field.
	 */
	private readResetTokenFromQuery(doc: Document | null): string {
		const search = doc?.defaultView?.location?.search ?? '';
		if (!search) {
			return '';
		}
		const raw = new URLSearchParams(search).get('reset') ?? '';
		const token = raw.trim();
		if (token === '' || /\s/.test(token) || token.length > 512) {
			return '';
		}
		return token;
	}

	public get authenticated(): boolean {
		return (
			this.betaAuth.isAuthenticated() &&
			(this.step === 'create-app' ||
				this.step === 'app' ||
				this.step === 'painted')
		);
	}

	public slugify(value: string): string {
		return (
			value
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/^-|-$/g, '') || 'happy-lab'
		);
	}

	public repoLabel(): string {
		return `${this.githubOrg}/${this.repository}`;
	}

	public hostedUrl(): string {
		return `${this.slugify(this.appName)}.inmotion.hosting`;
	}

	public goTo(step: AppLauncherStep): void {
		this.step = step;
		this.limitVisible = false;
	}

	public reset(): void {
		this.clearDeployTimers();
		this.step = 'landing';
		this.appTab = 'source';
		this.accountMode = 'create';
		this.deployIndex = 0;
		this.deployment = null;
		this.sourceConnected = false;
		this.limitVisible = false;
		this.feedbackOpen = false;
		this.feedbackSubmitted = false;
		this.interestSubmitted = false;
		this.accountEmail = '';
		this.sessionNotice = '';
		this.passwordResetToken = '';
	}

	public async logout(): Promise<void> {
		await this.betaAuth.logout();
		await this.firebaseAuth.signOut();
		this.reset();
	}

	/**
	 * Send the user back to sign-in when the Labs session can no longer load the
	 * apps surface. Keeps the email so re-entry is one password away, and never
	 * leaves a signed-in shell wrapped around a surface that cannot load.
	 */
	public async expireSession(notice = SESSION_EXPIRED_NOTICE): Promise<void> {
		this.clearDeployTimers();
		this.betaAuth.clearSession();
		await this.firebaseAuth.signOut();
		this.deployment = null;
		this.sourceConnected = false;
		this.deployIndex = 0;
		this.limitVisible = false;
		this.feedbackOpen = false;
		this.sessionNotice = notice;
		this.passwordResetToken = '';
		this.accountMode = 'sign-in';
		this.step = 'login';
	}

	public completePasswordReset(): void {
		this.betaAuth.clearSession();
		this.passwordResetToken = '';
		this.accountMode = 'sign-in';
		this.sessionNotice = PASSWORD_UPDATED_NOTICE;
		this.step = 'login';
	}

	public setTab(tab: AppLauncherTab): void {
		this.appTab = tab;
		if (this.step !== 'app') {
			this.step = 'app';
		}
	}

	public setAccountMode(mode: AccountMode): void {
		this.accountMode = mode;
		this.sessionNotice = '';
	}

	public startFromLanding(email: string): void {
		this.accountEmail = email.trim() || this.accountEmail;
		this.accountMode = 'create';
		this.goTo('login');
	}

	public completeLogin(email: string): void {
		this.accountEmail = email.trim() || this.accountEmail;
		this.interestForm.email = this.accountEmail;
		this.sessionNotice = '';
		this.step = 'create-app';
	}

	public createFirstApp(): void {
		this.step = 'app';
		this.appTab = 'source';
	}

	public addApp(): void {
		if (this.deployment) {
			this.step = 'create-app';
			this.limitVisible = true;
		} else {
			this.step = 'app';
			this.appTab = 'source';
		}
	}

	public connectSource(githubOrg: string): void {
		this.githubOrg = githubOrg.trim() || this.githubOrg;
		this.sourceConnected = true;
		this.appTab = 'source';
	}

	public runDeployment(): void {
		this.clearDeployTimers();
		this.sourceConnected = true;
		this.deployIndex = 0;
		this.deployment = null;
		this.appTab = 'logs';

		DEPLOY_STEPS.slice(1).forEach((_, index) => {
			this.deployTimers.push(
				setTimeout(
					() => {
						if (this.step !== 'app') {
							return;
						}
						this.deployIndex = index + 1;
					},
					650 * (index + 1),
				),
			);
		});

		this.deployTimers.push(
			setTimeout(() => {
				if (this.step !== 'app') {
					return;
				}
				this.deployIndex = DEPLOY_STEPS.length - 1;
				this.deployment = {
					appName: this.appName,
					status: 'Running',
					source: this.repoLabel(),
					branch: this.branch,
					url: this.hostedUrl(),
					deployedAt: 'Jul 28, 2026, 3:57 PM',
				};
				this.appTab = 'health';
			}, 3400),
		);
	}

	public removeDeployment(): void {
		this.clearDeployTimers();
		this.deployment = null;
		this.sourceConnected = false;
		this.deployIndex = 0;
		this.limitVisible = false;
		this.appTab = 'source';
		this.step = 'app';
	}

	public openLimit(): void {
		this.limitVisible = true;
	}

	public closeLimit(): void {
		this.limitVisible = false;
	}

	public openFeedback(): void {
		this.feedbackOpen = true;
		this.feedbackSubmitted = false;
	}

	public closeFeedback(): void {
		this.feedbackOpen = false;
	}

	public submitFeedback(
		feedbackType: string,
		feedbackText: string,
		feedbackEmail: string,
	): void {
		const type = feedbackType || 'General feedback';
		const text = feedbackText.trim() || 'No details provided';
		const email =
			feedbackEmail.trim() || this.accountEmail || 'Not provided';
		this.feedbackSubmitted = true;
		void this.postFeedbackToBetaApi(type, text, email);
	}

	public submitInterest(form: InterestForm): void {
		this.interestForm = {
			name: form.name.trim() || this.interestForm.name,
			email: form.email.trim() || this.accountEmail,
			capacity: form.capacity.trim() || this.interestForm.capacity,
			audience: form.audience || this.interestForm.audience,
			notes: form.notes.trim() || 'No notes provided',
		};
		this.interestSubmitted = true;
		void this.postInterestToBetaApi();
	}

	/** Persist interest via central-beta. */
	private async postInterestToBetaApi(): Promise<void> {
		const base = (environment.betaApiBase || '').trim().replace(/\/+$/, '');
		if (!base) {
			return;
		}
		try {
			await fetch(`${base}/interest`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					name: this.interestForm.name,
					email: this.interestForm.email,
					capacity: this.interestForm.capacity,
					audience: this.interestForm.audience,
					notes: this.interestForm.notes,
				}),
			});
		} catch (e) {
			console.error('[app-launcher] interest API error', e);
		}
	}

	/** Persist feedback via central-beta. */
	private async postFeedbackToBetaApi(
		type: string,
		text: string,
		email: string,
	): Promise<void> {
		const base = (environment.betaApiBase || '').trim().replace(/\/+$/, '');
		if (!base) {
			return;
		}
		try {
			await fetch(`${base}/feedback`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					type,
					text,
					email,
					prototypeState: this.currentProductState(),
				}),
			});
		} catch (e) {
			console.error('[app-launcher] feedback API error', e);
		}
	}

	private currentProductState(): string {
		if (this.step === 'app') {
			const label =
				TABS.find((t) => t.key === this.appTab)?.label ?? this.appTab;
			return `App Launcher / ${label}`;
		}
		if (this.step === 'painted') {
			return 'Expanded access request';
		}
		if (this.step === 'create-app') {
			return 'My Apps';
		}
		if (this.step === 'login') {
			return 'Account';
		}
		return 'Landing';
	}

	private clearDeployTimers(): void {
		for (const timer of this.deployTimers) {
			clearTimeout(timer);
		}
		this.deployTimers = [];
	}
}
